import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import MediaPreview from '../../../../components/media-preview/MediaPreview';
import RatingStars from '../../../../components/rating-stars/RatingStars';
import StatusBadge from '../../../../components/status-badge/StatusBadge';
import { Shot, getShotPlaybackSrc } from '../../../../app/services/shots';
import { SHOT_STATUS } from '../../../../constant/shotStatus';
import { useGetProjectMembershipQuery } from '../../../../app/accounts/memberships';
import { useGetCurrentUserQuery } from '../../../../app/accounts/authuser';
import { isProducerGroupPosition, isClientGroupPosition } from '../../../../utils/memberPositionUtils';

interface CompletedShotCardProps {
  shot: Shot;
  index: number;
}

const ShotContainer = styled.div.attrs({
  className: "u-relative u-row-center"
})`
  width: auto;
  height: auto;
  cursor: pointer;
`;

/**
 * 検索結果の完了ショットカード
 * AllShotsShotCard の完了表示と同じビジュアル（DnD不要のスタンドアロン版）
 */
const CompletedShotCard: React.FC<CompletedShotCardProps> = ({ shot, index }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { isMobile } = useSelector((state: any) => state.device);

  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId!, {
    skip: !projectId || !currentUserData,
  });

  const memberPosition = membershipData?.data?.position;
  const isClientGroup = memberPosition ? isClientGroupPosition(memberPosition) : false;
  const isProducerOrDirector = memberPosition ? isProducerGroupPosition(memberPosition) : false;

  const handlePreviewClick = useCallback(() => {
    if (projectId && shot.id) {
      navigate(`/${projectId}/${shot.id}`, {
        state: { shot }
      });
    }
  }, [navigate, projectId, shot]);

  const ratingCount = shot.ratings ? shot.ratings.length : 0;

  const renderRating = () => {
    if (isClientGroup) {
      return <RatingStars rating={shot.my_rating} isClickable={false} />;
    }
    return (
      <RatingStars
        rating={shot.average_rating}
        isClickable={false}
        showRatingText={isProducerOrDirector && ratingCount > 0}
        ratingCount={ratingCount}
      />
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <ShotContainer onClick={handlePreviewClick}>
        <MediaPreview
          src={getShotPlaybackSrc(shot) || shot.thumbnail || undefined}
          thumbnailSrc={shot.thumbnail || undefined}
          sizeMode={{ mode: 'fixed-height', height: isMobile ? '128px' : '192px' }}
          autoPlay={false}
          hoverPlay={!isMobile}
          controls={false}
          showStatusBar={false}
          showWaveform={false}
          disableHoverEffect={true}
        />
        <div className="shot-overlay">
          {renderRating()}
        </div>
        {shot.status === SHOT_STATUS.PRODUCER_DONE_CLIENT_UNAPPROVED && (
          <StatusBadge variant="overlay" label="保留" />
        )}
      </ShotContainer>
      <div className="u-row-between u-w100">
        <p translate="no">{shot.shot_name}</p>
      </div>
    </motion.div>
  );
};

export default CompletedShotCard;
